// SPDX-License-Identifier: AGPL-3.0-or-later

import {GuildBoostBadgeIcon} from '@app/features/guild/components/GuildBoostBadgeIcon';
import styles from '@app/features/guild/components/modals/guild_tabs/GuildServerTagTab.module.css';
import {
	ServerTagBadgeGlyph,
	SERVER_TAG_ALL_BADGE_IDS,
	SERVER_TAG_COLOR_PRESETS,
	SERVER_TAG_DEFAULT_BADGE_ID,
	SERVER_TAG_DEFAULT_COLOR,
	SERVER_TAG_FEATURED_BADGE_IDS,
	colorNumberToCss,
	type ServerTagBadgeId,
} from '@app/features/guild/components/server_tag/ServerTagBadgeIcons';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import GuildSettingsModalState from '@app/features/guild/state/GuildSettingsModal';
import Guilds from '@app/features/guild/state/Guilds';
import {
	SERVER_TAG_CHOOSE_BADGE_DESCRIPTOR,
	SERVER_TAG_CHOOSE_COLOR_DESCRIPTOR,
	SERVER_TAG_CHOOSE_NAME_DESCRIPTOR,
	SERVER_TAG_CUSTOM_COLOR_DESCRIPTOR,
	SERVER_TAG_DESCRIPTION_DESCRIPTOR,
	SERVER_TAG_LOCKED_HINT_DESCRIPTOR,
	SERVER_TAG_NAME_HINT_DESCRIPTOR,
	SERVER_TAG_PLACEHOLDER_DESCRIPTOR,
	SERVER_TAG_PREVIEW_MESSAGE_DESCRIPTOR,
	SERVER_TAG_PREVIEW_TITLE_DESCRIPTOR,
	SERVER_TAG_SAVE_FAILED_DESCRIPTOR,
	SERVER_TAG_SAVED_DESCRIPTOR,
	SERVER_TAG_SHOW_ALL_BADGES_DESCRIPTOR,
	SERVER_TAG_SHOW_FEWER_BADGES_DESCRIPTOR,
	SERVER_TAG_UNLOCK_WITH_BOOST_DESCRIPTOR,
	SERVER_TAG_UPDATE_NOTICE_DESCRIPTOR,
} from '@app/features/guild/utils/ServerTagMessageDescriptors';
import {PASSWORD_MANAGER_IGNORE_ATTRIBUTES} from '@app/features/platform/utils/PasswordManagerAutocomplete';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import * as UnsavedChangesCommands from '@app/features/ui/commands/UnsavedChangesCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import Users from '@app/features/user/state/Users';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {isServerTagBadgeId} from '@fluxer/constants/src/ServerTagConstants';
import {GUILD_BOOST_LEVEL_THRESHOLDS} from '@fluxer/constants/src/GuildBoostConstants';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import {useLingui} from '@lingui/react/macro';
import {CaretDownIcon, CaretUpIcon, EyedropperIcon, InfoIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const TAG_MAX_LENGTH = 4;
const SERVER_TAG_TAB_ID = 'server_tag';
const logger = new Logger('GuildServerTagTab');

function sanitizeTagInput(value: string): string {
	return value
		.toUpperCase()
		.replace(/[^A-Z0-9]/g, '')
		.slice(0, TAG_MAX_LENGTH);
}

function hexToColorNumber(hex: string): number {
	const clean = hex.replace('#', '');
	return parseInt(clean.slice(0, 6), 16) >>> 0;
}

function resolveBadgeId(value: string | null | undefined): ServerTagBadgeId {
	if (value && isServerTagBadgeId(value)) return value;
	return SERVER_TAG_DEFAULT_BADGE_ID;
}

const GuildServerTagTab: React.FC<{guildId: string}> = observer(({guildId}) => {
	const {i18n} = useLingui();
	const guild = Guilds.getGuild(guildId);
	const currentUser = Users.getCurrentUser();
	const isUnlocked = Boolean(guild?.features.has(GuildFeatures.SERVER_TAG));

	const remoteTagName = guild?.tagName ?? '';
	const remoteBadgeId = resolveBadgeId(guild?.tagBadgeId);
	const remoteColor = guild?.tagColor ?? SERVER_TAG_DEFAULT_COLOR;

	const [tagName, setTagName] = useState(remoteTagName || 'WUMP');
	const [badgeId, setBadgeId] = useState<ServerTagBadgeId>(remoteBadgeId);
	const [color, setColor] = useState(remoteColor);
	const [showAllBadges, setShowAllBadges] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		setTagName(remoteTagName || (remoteTagName === '' && !isUnlocked ? 'WUMP' : remoteTagName));
		setBadgeId(remoteBadgeId);
		setColor(remoteColor);
	}, [guildId, remoteTagName, remoteBadgeId, remoteColor, isUnlocked]);

	const badgeIds = showAllBadges ? SERVER_TAG_ALL_BADGE_IDS : SERVER_TAG_FEATURED_BADGE_IDS;
	const colorCss = colorNumberToCss(color);
	const previewName = currentUser
		? NicknameUtils.getDisplayName(currentUser)
		: i18n._(SERVER_TAG_PLACEHOLDER_DESCRIPTOR);
	const displayTag = tagName.length > 0 ? tagName : i18n._(SERVER_TAG_PLACEHOLDER_DESCRIPTOR);

	const hasUnsavedChanges = useMemo(() => {
		if (!isUnlocked) return false;
		const normalizedRemote = (remoteTagName || '').toUpperCase();
		return tagName !== normalizedRemote || badgeId !== remoteBadgeId || color !== remoteColor;
	}, [isUnlocked, tagName, badgeId, color, remoteTagName, remoteBadgeId, remoteColor]);

	const handleReset = useCallback(() => {
		setTagName(remoteTagName);
		setBadgeId(remoteBadgeId);
		setColor(remoteColor);
	}, [remoteTagName, remoteBadgeId, remoteColor]);

	const handleSave = useCallback(async () => {
		if (!isUnlocked || !guild) return;
		const normalized = sanitizeTagInput(tagName);
		if (normalized.length === 0) {
			ToastCommands.createToast({
				type: 'error',
				children: i18n._(SERVER_TAG_NAME_HINT_DESCRIPTOR),
			});
			return;
		}
		setIsSubmitting(true);
		try {
			await GuildCommands.update(guildId, {
				tag_name: normalized,
				tag_badge_id: badgeId,
				tag_color: color,
			});
			ToastCommands.createToast({type: 'success', children: i18n._(SERVER_TAG_SAVED_DESCRIPTOR)});
		} catch (error) {
			logger.error('Failed to save server tag', error);
			ToastCommands.createToast({type: 'error', children: i18n._(SERVER_TAG_SAVE_FAILED_DESCRIPTOR)});
		} finally {
			setIsSubmitting(false);
		}
	}, [isUnlocked, guild, tagName, badgeId, color, guildId, i18n]);

	useEffect(() => {
		UnsavedChangesCommands.setUnsavedChanges(SERVER_TAG_TAB_ID, hasUnsavedChanges);
	}, [hasUnsavedChanges]);

	useEffect(() => {
		UnsavedChangesCommands.setTabData(SERVER_TAG_TAB_ID, {
			onReset: handleReset,
			onSave: handleSave,
			isSubmitting,
		});
	}, [handleReset, handleSave, isSubmitting]);

	useEffect(() => {
		return () => {
			UnsavedChangesCommands.clearUnsavedChanges(SERVER_TAG_TAB_ID);
		};
	}, []);

	const handleTagChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setTagName(sanitizeTagInput(event.target.value));
	}, []);

	const handleUnlock = useCallback(() => {
		GuildSettingsModalState.navigateToTab(guildId, 'boost');
	}, [guildId]);

	const handleCustomColor = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setColor(hexToColorNumber(event.target.value));
	}, []);

	const isCustomColor = useMemo(() => !SERVER_TAG_COLOR_PRESETS.includes(color), [color]);

	return (
		<div className={styles.layout} data-flx="guild.guild-tabs.guild-server-tag-tab.layout">
			<div className={styles.mainColumn} data-flx="guild.guild-tabs.guild-server-tag-tab.main">
				<section className={styles.intro} data-flx="guild.guild-tabs.guild-server-tag-tab.intro">
					<p className={styles.description}>{i18n._(SERVER_TAG_DESCRIPTION_DESCRIPTOR)}</p>
					{!isUnlocked && (
						<>
							<Button
								variant="primary"
								className={styles.unlockButton}
								onClick={handleUnlock}
								leftIcon={<GuildBoostBadgeIcon size={16} />}
								data-flx="guild.guild-tabs.guild-server-tag-tab.unlock-button"
							>
								{i18n._(SERVER_TAG_UNLOCK_WITH_BOOST_DESCRIPTOR)}
							</Button>
							<p className={styles.unlockHint}>
								{i18n._(SERVER_TAG_LOCKED_HINT_DESCRIPTOR, {
									level: 2,
									boosts: GUILD_BOOST_LEVEL_THRESHOLDS[2],
								})}
							</p>
						</>
					)}
				</section>

				<section className={styles.section} data-flx="guild.guild-tabs.guild-server-tag-tab.name-section">
					<h3 className={styles.sectionTitle}>{i18n._(SERVER_TAG_CHOOSE_NAME_DESCRIPTOR)}</h3>
					<div
						className={clsx(styles.nameField, !isUnlocked && styles.nameFieldDisabled)}
						data-flx="guild.guild-tabs.guild-server-tag-tab.name-field"
					>
						<span className={styles.nameBadge} aria-hidden>
							<ServerTagBadgeGlyph badgeId={badgeId} size={22} accent={colorCss} />
						</span>
						<input
							className={styles.nameInput}
							value={tagName}
							onChange={handleTagChange}
							maxLength={TAG_MAX_LENGTH}
							placeholder={i18n._(SERVER_TAG_PLACEHOLDER_DESCRIPTOR)}
							spellCheck={false}
							autoComplete="off"
							disabled={!isUnlocked}
							{...PASSWORD_MANAGER_IGNORE_ATTRIBUTES}
							data-flx="guild.guild-tabs.guild-server-tag-tab.name-input"
						/>
					</div>
					<p className={styles.hint}>{i18n._(SERVER_TAG_NAME_HINT_DESCRIPTOR)}</p>
				</section>

				<div className={styles.infoNotice} role="note" data-flx="guild.guild-tabs.guild-server-tag-tab.info-notice">
					<InfoIcon className={styles.infoNoticeIcon} size={remFromPx(18)} weight="fill" aria-hidden />
					<span>{i18n._(SERVER_TAG_UPDATE_NOTICE_DESCRIPTOR)}</span>
				</div>

				<section className={styles.section} data-flx="guild.guild-tabs.guild-server-tag-tab.badge-section">
					<h3 className={styles.sectionTitle}>{i18n._(SERVER_TAG_CHOOSE_BADGE_DESCRIPTOR)}</h3>
					<div className={styles.badgeGrid} role="listbox" aria-label={i18n._(SERVER_TAG_CHOOSE_BADGE_DESCRIPTOR)}>
						{badgeIds.map((id) => {
							const selected = id === badgeId;
							return (
								<FocusRing key={id} offset={-2} enabled={isUnlocked}>
									<button
										type="button"
										role="option"
										aria-selected={selected}
										disabled={!isUnlocked}
										className={clsx(styles.badgeCell, selected && styles.badgeCellSelected)}
										onClick={() => setBadgeId(id)}
										data-flx={`guild.guild-tabs.guild-server-tag-tab.badge.${id}`}
									>
										<ServerTagBadgeGlyph badgeId={id} size={22} accent={colorCss} />
									</button>
								</FocusRing>
							);
						})}
					</div>
					<Button
						variant="ghost"
						small
						className={styles.expandButton}
						onClick={() => setShowAllBadges((value) => !value)}
						rightIcon={
							showAllBadges ? (
								<CaretUpIcon size={remFromPx(14)} weight="bold" />
							) : (
								<CaretDownIcon size={remFromPx(14)} weight="bold" />
							)
						}
						data-flx="guild.guild-tabs.guild-server-tag-tab.expand-badges"
					>
						{i18n._(
							showAllBadges ? SERVER_TAG_SHOW_FEWER_BADGES_DESCRIPTOR : SERVER_TAG_SHOW_ALL_BADGES_DESCRIPTOR,
						)}
					</Button>
				</section>

				<section className={styles.section} data-flx="guild.guild-tabs.guild-server-tag-tab.color-section">
					<h3 className={styles.sectionTitle}>{i18n._(SERVER_TAG_CHOOSE_COLOR_DESCRIPTOR)}</h3>
					<div className={styles.badgeGrid} role="listbox" aria-label={i18n._(SERVER_TAG_CHOOSE_COLOR_DESCRIPTOR)}>
						{SERVER_TAG_COLOR_PRESETS.map((preset) => {
							const selected = preset === color;
							const swatch = colorNumberToCss(preset);
							return (
								<FocusRing key={preset} offset={-2} enabled={isUnlocked}>
									<button
										type="button"
										role="option"
										aria-selected={selected}
										disabled={!isUnlocked}
										className={clsx(styles.colorCell, selected && styles.colorCellSelected)}
										onClick={() => setColor(preset)}
										data-flx={`guild.guild-tabs.guild-server-tag-tab.color.${preset.toString(16)}`}
									>
										<ServerTagBadgeGlyph badgeId={badgeId} size={22} accent={swatch} />
									</button>
								</FocusRing>
							);
						})}
						<FocusRing offset={-2} enabled={isUnlocked}>
							<label
								className={clsx(
									styles.colorCell,
									styles.customColorCell,
									isCustomColor && styles.colorCellSelected,
								)}
								title={i18n._(SERVER_TAG_CUSTOM_COLOR_DESCRIPTOR)}
								aria-label={i18n._(SERVER_TAG_CUSTOM_COLOR_DESCRIPTOR)}
								data-flx="guild.guild-tabs.guild-server-tag-tab.custom-color"
							>
								{isCustomColor ? (
									<ServerTagBadgeGlyph badgeId={badgeId} size={22} accent={colorCss} />
								) : (
									<EyedropperIcon size={remFromPx(18)} weight="bold" />
								)}
								<input
									type="color"
									value={colorCss}
									disabled={!isUnlocked}
									onChange={handleCustomColor}
									aria-label={i18n._(SERVER_TAG_CUSTOM_COLOR_DESCRIPTOR)}
								/>
							</label>
						</FocusRing>
					</div>
				</section>
			</div>

			<aside className={styles.previewColumn} data-flx="guild.guild-tabs.guild-server-tag-tab.preview-column">
				<div className={styles.previewCard} data-flx="guild.guild-tabs.guild-server-tag-tab.preview-card">
					<h3 className={styles.previewTitle}>{i18n._(SERVER_TAG_PREVIEW_TITLE_DESCRIPTOR)}</h3>
					<div className={styles.previewChat}>
						<div className={styles.previewMessage}>
							<div className={styles.previewAvatar} aria-hidden />
							<div className={styles.previewBody}>
								<div className={styles.previewNameRow}>
									<span className={styles.previewName}>{previewName}</span>
									<span className={styles.previewTag} data-flx="guild.guild-tabs.guild-server-tag-tab.preview-tag">
										<ServerTagBadgeGlyph badgeId={badgeId} size={12} accent={colorCss} />
										<span className={styles.previewTagText}>{displayTag}</span>
									</span>
								</div>
								<p className={styles.previewText}>{i18n._(SERVER_TAG_PREVIEW_MESSAGE_DESCRIPTOR)}</p>
							</div>
						</div>
					</div>
				</div>
			</aside>
		</div>
	);
});

export default GuildServerTagTab;
